import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SmartStaysLogo from '../components/SmartStaysLogo';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import API_URL from '../config';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './Contact.css';
import { toEmailInput, emailInputProps } from '../utils/emailInput';

function Contact() {
  const { t } = useTranslation();
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Adres opgesplitst in 4 losse velden i.p.v. 1 vrij tekstveld - duidelijker
  // in te vullen en leesbaarder in de interne e-mail dan 1 samengeraapte
  // "Straat, nummer, stad"-string.
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [service, setService] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [message, setMessage] = useState('');
  const [packageType, setPackageType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'SmartStays - Contact';

    // Check of er parameters zijn meegegeven via URL
    const params = new URLSearchParams(location.search);
    const pkg = params.get('package');
    const products = params.get('products');
    const msg = params.get('message');

    if (pkg) {
      setPackageType(pkg);
      setMessage(t('contact.interestedInPackage', { pkg }));
    }

    if (products) {
      setPackageType(products);
      setMessage(msg || t('contact.interestedInProducts', { products }));
    }
  }, [location, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateRequiredFields(e.target) || !name || !email) {
      setError(t('contact.fillRequired'));
      return;
    }

    if (message.length > 500) {
      setError(t('contact.messageTooLong'));
      return;
    }

    setError('');

    try {
      await axios.post(`${API_URL}/api/contact`, {
        name, email, phone, street, houseNumber, postalCode, city, service, propertyType, packageType, message
      });

      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(t('contact.somethingWrong'));
    }
  };

  if (submitted) {
    return (
      <div className="contact-container">
        <Navbar />
        <div className="contact-body">
          <div className="contact-card">
            <div className="contact-header">
              <h1><SmartStaysLogo /></h1>
              <p>{t('contact.requested')}</p>
            </div>
            <div className="success-message">
              <p>{t('contact.thanks', { name: name || '!' })}</p>
              <p>{t('contact.willContact')}</p>
            </div>
            <button
              onClick={() => setSubmitted(false)}
              className="back-btn"
              style={{ marginTop: '20px', backgroundColor: '#1e88e5', color: 'white' }}
            >
              {t('contact.newRequest')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="contact-container">
      <Navbar />
      <div className="contact-body">
        <div className="contact-grid">

          {/* Linkerkolom: contactgegevens */}
          <div className="contact-info-col">
            <h1><SmartStaysLogo /></h1>
            <p className="contact-info-subtitle">{t('contact.subtitle')}</p>

            <div className="promo-info">
              <div>
                <strong>{t('contact.promoTitle')}</strong>
                <p>{t('contact.promoText')}</p>
              </div>
            </div>

            <div className="reachability-info">
              <div className="reachability-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="white" strokeWidth="1.6"/>
                  <path d="M4 7L12 13L20 7" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <strong>{t('contact.reachabilityTitle')}</strong>
                <p>{t('contact.reachabilityText')}</p>
              </div>
            </div>

            <div className="contact-details">
              <h3>{t('contact.infoTitle')}</h3>
              <ul>
                <li>
                  <span className="contact-details-label">{t('contact.infoEmail')}</span>
                  <a href="mailto:info@smartstays.be">info@smartstays.be</a>
                </li>
                <li>
                  <span className="contact-details-label">{t('contact.infoCompanyNumber')}</span>
                  <span>1039.968.771</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Rechterkolom: contactformulier */}
          <div className="contact-form-col">
            <div className="contact-card">
              {packageType && (
                <div className="selected-package">
                  <strong>{t('contact.selected')}:</strong> {packageType}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate onInput={clearFieldInvalid}>
                <div className="form-group">
                  <label>{t('contact.name')} *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('contact.email')} *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(toEmailInput(e.target.value))}
                    {...emailInputProps}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('contact.phone')}</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>{t('contact.address')}</label>
                  <div className="form-row">
                    <div className="form-group form-group-wide">
                      <label className="form-subgroup-label">{t('contact.street')}</label>
                      <input
                        type="text"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        aria-label={t('contact.street')}
                      />
                    </div>
                    <div className="form-group form-group-narrow">
                      <label className="form-subgroup-label">{t('contact.houseNumber')}</label>
                      <input
                        type="text"
                        value={houseNumber}
                        onChange={(e) => setHouseNumber(e.target.value)}
                        aria-label={t('contact.houseNumber')}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group form-group-narrow">
                      <label className="form-subgroup-label">{t('contact.postalCode')}</label>
                      <input
                        type="text"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        aria-label={t('contact.postalCode')}
                      />
                    </div>
                    <div className="form-group form-group-wide">
                      <label className="form-subgroup-label">{t('contact.city')}</label>
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        aria-label={t('contact.city')}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('contact.service')}</label>
                  <select
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                  >
                    <option value="">{t('contact.serviceGeneral')}</option>
                    <option value="toegangsbeheer">{t('pricing.tabs.accessManagement')}</option>
                    <option value="schoonmaak">{t('pricing.tabs.cleaning')}</option>
                    <option value="automation">{t('pricing.tabs.automation')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('contact.propertyType')}</label>
                  <select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e.target.value)}
                  >
                    {/* Hergebruikt bewust dezelfde i18n-keys als de prijstabel op
                        Aanbod > Schoonmaak (pricing.cleaningTable.*) i.p.v. eigen
                        contact.type*-teksten - zo kunnen de namen hier nooit meer
                        uit sync raken met de "officiële" namen daar. */}
                    <option value="">{t('contact.selectType')}</option>
                    <option value="hotelkamer">{t('pricing.cleaningTable.hotelRoom')}</option>
                    <option value="appartement1">{t('pricing.cleaningTable.type2')}</option>
                    <option value="appartement2">{t('pricing.cleaningTable.type3')}</option>
                    <option value="woning">{t('pricing.cleaningTable.type4')}</option>
                    <option value="villa">{t('pricing.cleaningTable.type5')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('contact.message')}</label>
                  <textarea
                    rows="4"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength="500"
                  />
                  <div className="char-counter">
                    {message.length}/500 {t('contact.characters')}
                  </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <button type="submit" className="submit-btn">{t('contact.submit')}</button>
              </form>
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Contact;
